import * as Sentry from "@sentry/react";
import { signOut } from "firebase/auth";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { auth } from "../auth/config";
import { LoadingScreen } from "../components/loading-screen";
import { requestKioskBootstrap } from "./kiosk-bootstrap";
import { installKioskBridge, postNativeEvent } from "./kiosk-bridge";
import {
  hasKioskNativeShell,
  KioskLaunch,
  SupportedKioskLanguage,
} from "./kiosk-launch";
import { KioskTerminalView } from "./kiosk-terminal-view";

type KioskContextValue = {
  isKiosk: boolean;
  hasNativeShell: boolean;
  completeProfileCreation(): void;
};

type KioskTerminal = "closing" | "profile-created-fallback" | null;

const KioskContext = createContext<KioskContextValue | undefined>(undefined);

type Props = {
  children: React.ReactNode;
};

export const KioskProvider = ({ children }: Props) => {
  const { i18n } = useTranslation();
  const [hasNativeShell] = useState(() => hasKioskNativeShell(window));
  const [launch, setLaunch] = useState<KioskLaunch | null>(() =>
    hasNativeShell
      ? null
      : { isKiosk: false, hasNativeShell: false, language: null },
  );
  const [bootstrapFailed, setBootstrapFailed] = useState(false);
  const [terminal, setTerminal] = useState<KioskTerminal>(null);
  const closingRef = useRef(false);
  const profileCompletionRef = useRef(false);

  const applyLanguage = useCallback(
    async (language: SupportedKioskLanguage) => {
      await i18n.changeLanguage(language);
      document.documentElement.lang = language;
    },
    [i18n],
  );

  const requestClose = useCallback(async () => {
    if (closingRef.current) return;
    closingRef.current = true;
    setTerminal("closing");

    try {
      await signOut(auth);
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      const handled = postNativeEvent(window.closeHandler, "closed");
      if (!handled) {
        closingRef.current = false;
        setTerminal(null);
      }
    }
  }, []);

  const completeProfileCreation = useCallback(async () => {
    if (profileCompletionRef.current) return;
    profileCompletionRef.current = true;

    try {
      await signOut(auth);
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      const handled = postNativeEvent(
        window.profileCreatedHandler,
        "profile-created",
      );
      if (!handled) {
        setTerminal("profile-created-fallback");
      }
    }
  }, []);

  useEffect(() => {
    if (!hasNativeShell) return;

    let isActive = true;

    const initialize = async () => {
      try {
        const nextLaunch = await requestKioskBootstrap(window);
        const language = nextLaunch.language;
        if (!language) {
          throw new Error("Kiosk bootstrap language is missing");
        }

        await applyLanguage(language);
        if (isActive) setLaunch(nextLaunch);
      } catch (error) {
        if (!isActive) return;
        Sentry.captureException(error);
        setBootstrapFailed(true);
      }
    };

    void initialize();

    return () => {
      isActive = false;
    };
  }, [applyLanguage, hasNativeShell]);

  useEffect(() => {
    if (!launch?.hasNativeShell) return;

    return installKioskBridge(window, {
      setLanguage: (language) => void applyLanguage(language),
      requestClose: () => void requestClose(),
    });
  }, [applyLanguage, launch, requestClose]);

  if (bootstrapFailed) {
    return (
      <KioskTerminalView
        titleKey="kiosk.authInitialization.title"
        bodyKey="kiosk.authInitialization.body"
        actionKey="kiosk.authInitialization.retry"
        onAction={() => window.location.reload()}
      />
    );
  }

  if (!launch) {
    return <LoadingScreen />;
  }

  if (terminal === "closing") {
    return <KioskTerminalView titleKey="kiosk.closing" />;
  }

  if (terminal === "profile-created-fallback") {
    return (
      <KioskTerminalView
        titleKey="kiosk.profileCreatedFallback.title"
        bodyKey="kiosk.profileCreatedFallback.body"
      />
    );
  }

  return (
    <KioskContext.Provider
      value={{
        isKiosk: launch.isKiosk,
        hasNativeShell: launch.hasNativeShell,
        completeProfileCreation: () => void completeProfileCreation(),
      }}
    >
      {children}
    </KioskContext.Provider>
  );
};

export const useKiosk = () => {
  const context = useContext(KioskContext);
  if (!context) {
    throw new Error("useKiosk must be used within KioskProvider");
  }
  return context;
};
